import { tz, tzOffset } from '@date-fns/tz';
import { format } from 'date-fns';

/**
 * Market time zones.
 *
 * The admin shows and takes every date and time in a market's zone, never the browser's: the
 * selected market for its pages, a record's own market on its detail page and edit dialog, and
 * Palestine for "All markets", for pages that are not about one market, and for records with no
 * market. The zone names below are the only place the labels are written, so a translation is
 * one more map.
 */

/** Palestine time: All markets, pages that are not market-specific, and records with no market. */
export const DEFAULT_TIME_ZONE = 'Asia/Hebron';

/** Whether this browser knows the zone id (an unknown id would make every date format throw). */
export function isKnownZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

const ZONE_NAMES: Readonly<Record<string, string>> = {
  'Asia/Hebron': 'Palestine time',
  'Asia/Gaza': 'Palestine time',
  'Asia/Damascus': 'Syria time',
};

const ALL_MARKETS = 'All markets';

/** "Palestine time"; an unknown zone is named by its id. */
export function zoneName(zone: string): string {
  return ZONE_NAMES[zone] ?? zone;
}

/** "UTC+02:00": the zone's offset in force at `at`. */
export function zoneOffset(zone: string, at: Date = new Date()): string {
  return formatOffset(tzOffset(zone, at));
}

/** "UTC+03:00" for 180 minutes. */
export function formatOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const mins = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hours}:${mins}`;
}

/** "Palestine time (UTC+02:00)", with the offset in force at `at` (now by default). */
export function zoneLabel(zone: string, at: Date = new Date()): string {
  return `${zoneName(zone)} (${zoneOffset(zone, at)})`;
}

/** "Syria time (UTC+03:00 on 12 Nov 2026)": the offset on the given day, for date-time fields. */
export function zoneLabelOn(zone: string, at: Date): string {
  return `${zoneName(zone)} (${zoneOffset(zone, at)} on ${format(at, 'd MMM yyyy', { in: tz(zone) })})`;
}

/** "All markets · Palestine time (UTC+02:00)", for a page-level All scope. */
export function allMarketsLabel(at: Date = new Date()): string {
  return `${ALL_MARKETS} · ${zoneLabel(DEFAULT_TIME_ZONE, at)}`;
}

/** The note a page that is not about one market adds to its description: "· Palestine time". */
export const NON_MARKET_TIME_NOTE = `· ${zoneName(DEFAULT_TIME_ZONE)}`;

/** The market's calendar day of an instant, as yyyy-MM-dd. */
export function marketDay(instant: Date, zone: string): string {
  return format(instant, 'yyyy-MM-dd', { in: tz(zone) });
}

/** Today in the market, as yyyy-MM-dd. */
export function marketToday(zone: string, now: Date = new Date()): string {
  return marketDay(now, zone);
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * A yyyy-MM-dd day moved by whole days. Days are calendar values, so the arithmetic is done on
 * the date alone and no zone can shift it.
 */
export function addDaysToDay(day: string, days: number): string {
  const match = DAY.exec(day);
  if (!match) return day;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days));
  return date.toISOString().slice(0, 10);
}

/** The parts of a yyyy-MM-dd day, or null for anything else. */
export function dayParts(day: string): { year: number; month: number; day: number } | null {
  const match = DAY.exec(day);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}
